import React from 'react';
import { Lock, X } from 'lucide-react';
import Logo from '../../../components/Logo/Logo';

// Two-level app navigation, sitting below the global top bar.
//   Level 1 — a 58px icon rail, one tile per section (16px glyph over a 10px
//             label, active tile gets a rounded-square highlight).
//   Level 2 — a 216px panel listing the selected section's items as 32px rows.
// Selecting a rail tile only swaps the panel; navigation happens in the panel.

function RailTile({ section, active, onSelect }) {
  const Icon = section.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(section.key)}
      aria-current={active ? 'true' : undefined}
      className={`tg-rail-item ${active ? 'is-active' : ''}`}
    >
      <span className="tg-rail-tile"><Icon size={16} /></span>
      <span className="tg-rail-label">{section.name}</span>
    </button>
  );
}

function NavRow({ item, active, onNavigate, onCloseMobile }) {
  const Icon = item.icon;
  const locked = item.locked === true;
  // Not yet offered. Renders dimmed and inert - distinct from `locked`, which
  // is a plan gate and does navigate (to billing).
  const soon = item.soon === true;
  // A few destinations (the tracker download) live outside the dashboard
  // shell entirely — opening them in a new tab keeps the admin's place here.
  const handleClick = item.newTab
    ? () => { window.open(item.href, '_blank', 'noopener,noreferrer'); if (onCloseMobile) onCloseMobile(); }
    : () => onNavigate(item.href, locked);
  const label = soon ? `${item.name} - coming soon` : item.name;
  return (
    <button
      type="button"
      onClick={soon ? undefined : handleClick}
      disabled={soon}
      aria-disabled={soon || undefined}
      title={soon ? 'Coming soon' : undefined}
      aria-label={soon ? label : undefined}
      className={`tg-nav-item ${active ? 'is-active' : ''}`}
    >
      <Icon className="tg-nav-icon mr-3 h-4 w-4" />
      <span className="flex-1 truncate text-left">{item.name}</span>
      {locked && <Lock className="tg-nav-icon ml-2 h-3.5 w-3.5" />}
      {item.badge && !locked && (
        <span className={`tg-count-pill ml-2 flex-shrink-0${item.badge === 'new' ? ' tg-count-pill--new' : ''}`}>
          {item.badge}
        </span>
      )}

    </button>
  );
}

// Section items split into the primary entry and the object list below the
// hairline — the same rhythm as Asana's Home/Inbox vs My tasks/Projects split.
function SectionItems({ section, isActive, onNavigate, onCloseMobile }) {
  const primary = section.items.filter((i) => i.primary);
  const rest = section.items.filter((i) => !i.primary);
  return (
    <>
      {primary.map((item) => (
        <NavRow key={item.href} item={item} active={isActive(item.href)} onNavigate={onNavigate} onCloseMobile={onCloseMobile} />
      ))}
      {primary.length > 0 && rest.length > 0 && <div className="tg-nav-divider" />}
      {rest.map((item) => (
        <NavRow key={item.href} item={item} active={isActive(item.href)} onNavigate={onNavigate} onCloseMobile={onCloseMobile} />
      ))}
    </>
  );
}

// Account, theme and trial live in the top bar; this panel is navigation only.
export function AppSidebar({
  sections, activeSection, onSelectSection, isActive, onNavigate,
  collapsed, mobileOpen, onCloseMobile, footerItem,
}) {
  // Sections flagged `pinBottom` (Settings) sit against the rail floor, away
  // from the module tiles, so a mis-aimed click can't drop you into config.
  const railTop = sections.filter((s) => !s.pinBottom);
  const railBottom = sections.filter((s) => s.pinBottom);
  const rail = (
    <div className="tg-rail">
      <div className="tg-rail-group">
        {railTop.map((s) => (
          <RailTile key={s.key} section={s} active={s.key === activeSection?.key} onSelect={onSelectSection} />
        ))}
      </div>
      {railBottom.length > 0 && (
        <div className="tg-rail-group tg-rail-group--bottom">
          {railBottom.map((s) => (
            <RailTile key={s.key} section={s} active={s.key === activeSection?.key} onSelect={onSelectSection} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* ── Mobile drawer: rail collapses away, every section stacks ── */}
      <div className={`md:hidden ${mobileOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-slate-900/40 dark:bg-slate-900/60" onClick={onCloseMobile} />
          <div className="tg-sidebar relative flex w-full max-w-xs flex-1 flex-col">
            <div className="absolute right-0 top-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none"
                onClick={onCloseMobile}
              >
                <X className="h-6 w-6 text-app-primary" />
              </button>
            </div>
            <div className="flex h-11 flex-shrink-0 items-center border-b border-app-subtle px-3">
              <Logo size={28} showText={false} />
            </div>
            <div className="h-0 flex-1 overflow-y-auto px-2 py-3">
              {sections.map((s) => (
                <div key={s.key} className="mb-3">
                  <div className="tg-nav-label mb-1">{s.name}</div>
                  <div className="space-y-0.5">
                    <SectionItems section={s} isActive={isActive} onNavigate={onNavigate} onCloseMobile={onCloseMobile} />
                  </div>
                </div>
              ))}
            </div>
            {/* Static footer — outside the scroller, so it's the same row on
                every section instead of scrolling away with the sections above. */}
            {footerItem && (
              <div className="flex-shrink-0 border-t border-app-subtle px-2 py-2">
                <NavRow item={footerItem} active={isActive(footerItem.href)} onNavigate={onNavigate} onCloseMobile={onCloseMobile} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Desktop: icon rail + section panel ── */}
      <div className="relative hidden md:flex md:flex-shrink-0">
        {rail}
        <div className={`flex flex-col transition-[width] duration-200 ${collapsed ? 'w-0 overflow-hidden' : 'w-[216px]'}`}>
          {!collapsed && (
            <div className="tg-sidebar flex h-0 flex-1 flex-col">
              <nav className="flex-1 overflow-y-auto px-2.5 pb-4 pt-4">
                <div className="tg-nav-label mb-1.5">{activeSection?.name}</div>
                <div className="space-y-0.5">
                  {activeSection && (
                    <SectionItems section={activeSection} isActive={isActive} onNavigate={onNavigate} />
                  )}
                </div>
              </nav>
              {/* Static footer — outside the scroller, so it stays in the same
                  spot under every rail tile's panel instead of living inside
                  just one section. */}
              {footerItem && (
                <div className="flex-shrink-0 border-t border-app-subtle px-2.5 py-2">
                  <NavRow item={footerItem} active={isActive(footerItem.href)} onNavigate={onNavigate} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
